import { Injectable } from '@angular/core'
import { ShopsService } from '../../shops.service'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { tap } from 'rxjs/operators'
import {
  AdminDeclineShop,
  AdminVerifyShop,
  GetAllAdminShops,
  GetOneAdminShop,
  UpdateAdminShop,
  AdminDeleteShopUser,
  GetAdminDailyReportsShops,
  SetAdminShopPosType,
  AdminDeleteShop,
  UpdateAdminShopTagSize,
  GetAdminShopOperations,
  UpdateAdminShopOperations,
  GetAllAdminGroups,
  GetUnimportedAdminShops
} from './admin-shops.actions'
import { AdminShopsStateModel } from './admin-shops.model'
import { IPaginationResponse } from '@mg-platform/core/core-data-access'

@State<AdminShopsStateModel>({
  name: 'adminShops'
})
@Injectable()
export class AdminShopsState {
  constructor(private shopsService: ShopsService) {}

  @Selector()
  static shop(state: AdminShopsStateModel) {
    return state.shop
  }

  static shopUsers(state: AdminShopsStateModel) {
    return state.shop?.users ?? []
  }

  @Selector()
  static allShops(state: AdminShopsStateModel) {
    return state.allShops
  }

  @Selector()
  static allShopsResponse(state: AdminShopsStateModel) {
    return state.allShopsResponse
  }

  @Selector()
  static unimportedShopsResponse(state: AdminShopsStateModel) {
    return state.unimportedShopsResponse
  }

  @Selector()
  static shopOperations(state: AdminShopsStateModel) {
    return state.shopOperations
  }

  @Selector()
  static allGroupsResponse(state: AdminShopsStateModel) {
    return state.allGroupsResponse
  }

  @Action(GetAllAdminShops)
  getAdminShops(
    ctx: StateContext<AdminShopsStateModel>,
    { info, pageSize, organizationId, userId, excludeBatch }: GetAllAdminShops
  ) {
    ctx.patchState({
      shop: undefined
    })
    return this.shopsService.getAllAdminShops(info, pageSize, organizationId, userId, excludeBatch).pipe(
      tap((res) => {
        ctx.patchState({
          allShopsResponse: res
        })
      })
    )
  }

  @Action(GetUnimportedAdminShops)
  getUnimportedAdminShops(
    ctx: StateContext<AdminShopsStateModel>,
    { info, pageSize }: GetUnimportedAdminShops
  ) {
    ctx.patchState({
      unimportedShopsResponse: undefined
    })
    return this.shopsService.getUnimportedAdminShops(info, pageSize).pipe(
      tap((res) => {
        ctx.patchState({
          unimportedShopsResponse: res
        })
      })
    )
  }

  @Action(GetOneAdminShop)
  getOneAdminShop(ctx: StateContext<AdminShopsStateModel>, { shopId }: GetOneAdminShop) {
    return this.shopsService.getOneAdminShop(shopId).pipe(
      tap((res) => {
        ctx.patchState({ shop: res })
      })
    )
  }

  @Action(UpdateAdminShop, { cancelUncompleted: true })
  updateAdminShop(ctx: StateContext<AdminShopsStateModel>, { payload, shopId }: UpdateAdminShop) {
    return this.shopsService.updateAdminShop(payload, shopId)
  }

  @Action(SetAdminShopPosType)
  setAdminShopPosType(ctx: StateContext<AdminShopsStateModel>, { posType }: SetAdminShopPosType) {
    const shop = ctx.getState()?.shop
    if (shop) {
      ctx.patchState({
        shop: {
          ...shop,
          posType
        }
      })
    }
  }

  @Action(AdminVerifyShop)
  adminVerifyShop(ctx: StateContext<AdminShopsStateModel>, { shopId }: AdminVerifyShop) {
    return this.shopsService.adminVerifyShop(shopId)
  }

  @Action(AdminDeclineShop)
  adminDeclineShop(ctx: StateContext<AdminShopsStateModel>, { shopId }: AdminDeclineShop) {
    return this.shopsService.adminDeclineShop(shopId)
  }

  @Action(AdminDeleteShop)
  adminDeleteShop(ctx: StateContext<AdminShopsStateModel>, { shopId }: AdminDeleteShop) {
    return this.shopsService.adminDeleteShop(shopId)
  }

  @Action(AdminDeleteShopUser)
  adminDeleteShopUser(ctx: StateContext<AdminShopsStateModel>, { shopId, userId }: AdminDeleteShopUser) {
    return this.shopsService.adminDeleteShopUser(shopId, userId)
  }

  @Action(GetAdminDailyReportsShops)
  getAdminDailyReportsShops(
    ctx: StateContext<AdminShopsStateModel>,
    { payload, excludeBatch }: GetAdminDailyReportsShops
  ) {
    return this.shopsService.getDailyReportsShops(payload, excludeBatch).pipe(
      tap((res) => {
        ctx.patchState({
          allShops: res.shops
        })
      })
    )
  }

  private static getFullPaginationInfo(info: IPaginationResponse) {
    return {
      ...info,
      totalPages: info.totalResults / info.pageSize + 1
    }
  }

  @Action(UpdateAdminShopTagSize)
  updateAdminShopTagSize(ctx: StateContext<AdminShopsStateModel>, { payload }: UpdateAdminShopTagSize) {
    return this.shopsService.updateAdminShopTagSize(payload)
  }

  @Action(GetAdminShopOperations)
  getShopOperations(ctx: StateContext<AdminShopsStateModel>, { shopId }: GetAdminShopOperations) {
    return this.shopsService.getShopOperations(shopId).pipe(
      tap((res) => {
        ctx.patchState({ shopOperations: res })
      })
    )
  }

  @Action(UpdateAdminShopOperations)
  updateShopOperations(
    ctx: StateContext<AdminShopsStateModel>,
    { payload, shopId }: UpdateAdminShopOperations
  ) {
    return this.shopsService.updateShopOperations(payload, shopId)
  }

  @Action(GetAllAdminGroups)
  getAdminGroups(
    ctx: StateContext<AdminShopsStateModel>,
    { info, pageSize, organizationId }: GetAllAdminGroups
  ) {
    ctx.patchState({
      shop: undefined
    })
    return this.shopsService.getAllAdminGroups(info, pageSize, organizationId).pipe(
      tap((res) => {
        ctx.patchState({
          allGroupsResponse: res
        })
      })
    )
  }
}
